import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';

import click from '@gojob/tracking/click';
import Cta from '@gojob/ui-bits/Cta';
import { useMatchBreakpoint } from '@gojob/ui-bits/hooks/useBreakpoint';
import useIsHydrated from '@gojob/ui-bits/hooks/useIsHydrated';
import SalaryDetails from '@gojob/ui-bits/JobCard/SalaryDetails';
import JobCardTranslationProvider from '@gojob/ui-bits/JobCard/TranslationProvider';
import TranslationProvider from '@gojob/ui-bits/ResponsiveJobCard/TranslationProvider';
import useTheme from '@gojob/ui-bits/styles/useTheme';

import JobCard from 'src/components/JobCard';
import Unavailable from 'src/components/OfferView/Unavailable';
import useComputerSkills from 'src/components/OfferView/useComputerSkills';
import useLanguageSkills from 'src/components/OfferView/useLanguageSkills';
import useLink from 'src/components/OfferView/useLink';
import useOffer from 'src/components/OfferView/useOffer';
import useSlugIds from 'src/components/OfferView/useSlugIds';
import useTimeTables from 'src/components/OfferView/useTimeTables';
import Seo from 'src/components/Seo';
import searchTracking from 'src/tracking/search';
import useTrackOfferEvent from 'src/tracking/useTrackOfferEvent';
import useTrackPageview from 'src/tracking/useTrackPageview';
import useTranslation from 'src/translations/useTranslation';
import { Job } from 'src/types/gojob';

export type OfferViewProps = {
  path?: string;
  slug?: string;
  job?: Job;
};

const useStyles = () => {
  const { spacing, colors } = useTheme();
  return {
    job: {
      width: '100%',
      // little trick to avoid the gap
      // between the wave af the menu
      // and the header of the card
      borderTop: `${spacing(4)} solid ${colors.primary}`,
      marginTop: spacing(-4),
    },
    buttonContainer: {
      position: 'sticky',
      bottom: spacing(8),
      width: `calc(100% - ${spacing(8)})`,
      maxWidth: 500,
      right: 0,
      left: 0,
      margin: `${spacing(3)} auto`,
    },
    icon: {
      marginRight: spacing(1),
    },
    button: {
      textDecoration: 'none',
    },
  } as const;
};

const OfferView: FunctionComponent<OfferViewProps> = (props) => {
  const { slug } = props;

  const ids = useSlugIds(slug);

  const [open, setOpen] = useState(false);

  const variant = useMatchBreakpoint(['xs', 'sm']) ? 'mobile' : 'desktop';
  const t = useTranslation();
  const styles = useStyles();

  const isHydrated = useIsHydrated();

  /** Do not refetch if job is passed as props */
  const { data, loading, error } = useOffer(ids || {}, !!props.job);

  const handleOnSalaryClick = () => {
    if (loading) {
      return;
    }
    click({
      eventAction: 'Open salary tooltip',
      eventCategory: 'offer-jobboard',
      eventName: 'Open salary tooltip on job offer detail',
    });
    setOpen(true);
  };

  const job = data?.job || props?.job || { ...ids };

  useTrackPageview({}, !!loading || !!error);

  const trackOfferEvent = useTrackOfferEvent(job);
  useEffect(() => {
    if (!loading && !error) {
      trackOfferEvent('productDetail');
    }
  }, [trackOfferEvent]);

  const languageSkills = useLanguageSkills(job);
  const computerSkills = useComputerSkills(job);
  const timeTables = useTimeTables(job);

  const title = useMemo(() => {
    const { caption, city } = job || {};
    if (caption && city) {
      return t('our_jobs_{title}_in_{city}', { city, title: caption });
    }
  }, [job, t]);

  const description = useMemo(() => {
    const { caption, city } = job || {};
    if (caption && city) {
      return t('looking_for_{title}_in_{city}', { city, title: caption });
    }
  }, [job, t]);

  if (error) {
    return <Unavailable />;
  }

  const link = useLink(job.id);

  const handleOnClick = () => {
    searchTracking.clickOnJobApply();
    trackOfferEvent('productAddToCart');
  };

  return (
    <>
      <Seo title={title} description={description} />
      {isHydrated && (
        <JobCardTranslationProvider translationFunction={t}>
          <SalaryDetails
            style={{ zIndex: 1000 }}
            padding={{ xs: [10, 0, 0], md: 0 }}
            open={open}
            variant={variant}
            onClose={() => setOpen(false)}
            monthlyCP={job.salary?.monthlyCP}
            totalCP={job.salary?.totalCP}
            totalIFM={job.salary?.totalIFM}
            totalSalary={job.salary?.totalSalary}
            monthlySalary={job.salary?.monthlySalary}
          />
        </JobCardTranslationProvider>
      )}
      <TranslationProvider translationFunction={t}>
        <JobCard
          css={styles.job}
          jobId={job?.id}
          onSalaryClick={handleOnSalaryClick}
          loading={loading}
          cityName={job.city}
          postalCode={job.postalCode}
          startDate={job.from}
          endDate={job.to}
          jobLabel={job.caption}
          updateDate={job.publishedAt}
          weeklyHours={job.weeklyHours}
          grossHourlyRate={job.salary?.grossHourlyRate}
          grossHourlyRateIncludingCP={job.salary?.grossHourlyRateIncludingCP}
          certificationLicences={job.certifications}
          languageSkills={languageSkills}
          computerSkills={computerSkills}
          timeTables={timeTables}
          tasks={job.activitiesDetail}
          advertisement={job.advertisement}
          profile={job.wantedProfile}
          latitude={job.location?.lat}
          longitude={job.location?.lon}
          radius={1000}
          googleMapsApiKey={process.env.GOOGLE_MAPS_API_KEY}
        />
      </TranslationProvider>

      <div css={styles.buttonContainer}>
        <Cta
          data-testid="ApplyButton"
          as="a"
          href={link}
          css={styles.button}
          onClick={handleOnClick}
          variant="primary"
          icon="ThumbsUp"
        >
          {t('apply')}
        </Cta>
      </div>
    </>
  );
};

export default OfferView;
